import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ManagerPinModal from "@/components/ManagerPinModal";
import PickupScanner from "@/components/PickupScanner";
import { PackageCheck, Search, RotateCcw, Lock, CheckCircle2, Clock, PackageOpen, ScanLine, Truck, Printer } from "lucide-react";
import { printYardTicket } from "@/lib/print";

export const fulfillmentBadge = (s) => ({
  awaiting_pickup: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  awaiting_delivery: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30",
  awaiting_fulfillment: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  partially_picked_up: "bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30",
  partially_delivered: "bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30",
  partially_fulfilled: "bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30",
  picked_up: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  delivered: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  fulfilled: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  closed: "bg-slate-500/15 text-slate-500 border-slate-500/30",
}[s] || "");

const DONE = ["picked_up", "delivered", "fulfilled", "closed"];
const METHOD = { take_now: { label: "Collected Now", cls: "text-emerald-600" }, pickup: { label: "Pickup", cls: "text-amber-600" }, delivery: { label: "Delivery", cls: "text-sky-600" } };

const TABS = [
  { key: "awaiting_pickup", label: "Awaiting Pickup", icon: Clock },
  { key: "awaiting_delivery", label: "Awaiting Delivery", icon: Truck },
  { key: "partial", label: "Partial", icon: PackageOpen },
  { key: "completed", label: "Completed", icon: CheckCircle2 },
];

export default function Pickups() {
  const { user, business } = useAuth();
  const isMgr = user?.role === "owner" || user?.role === "manager";
  const qrMode = business?.pickup_verification_mode === "qr";
  const [scanOpen, setScanOpen] = useState(false);
  const [tab, setTab] = useState(() => { const t = new URLSearchParams(window.location.search).get("tab"); return TABS.some((x) => x.key === t) ? t : "awaiting_pickup"; });
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [rel, setRel] = useState({});
  const [collectedBy, setCollectedBy] = useState("");
  const [note, setNote] = useState("");
  const [closeReason, setCloseReason] = useState("");
  const [closeMode, setCloseMode] = useState(false);
  const [pin, setPin] = useState(null); // { action, payload, title, reason }
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/pickups", { params: { status: q.trim() ? "" : tab, q } }).then((r) => setRows(r.data)).catch(() => {});
  }, [tab, q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const open = async (inv) => {
    try {
      const { data } = await api.get(`/pickups/${inv.id}`);
      setDetail(data); setRel({}); setCollectedBy(""); setNote(""); setCloseMode(false); setCloseReason("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const afterAction = (data, msg) => { setDetail(data); setRel({}); setCollectedBy(""); setNote(""); setCloseMode(false); setPin(null); load(); toast.success(msg); };

  const onScan = useCallback(async (code) => {
    try {
      const { data } = await api.post("/pickups/scan", { code });
      setScanOpen(false);
      setDetail(data); setRel({}); setCollectedBy(""); setNote(""); setCloseMode(false); setCloseReason("");
      if (data.already_complete) toast.warning(`${data.pickup_reference} is already ${data.fulfillment_label}. Nothing left to release.`);
      else toast.success(`Opened ${data.pickup_reference}: ${data.remaining_total_qty} unit(s) remaining`);
    } catch (e) { toast.error("Scan rejected", { description: apiError(e.response?.data?.detail) }); }
  }, []);

  const run = async (action, payload, managerPin = null) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pickups/${detail.id}/${action}`, { ...payload, manager_pin: managerPin });
      afterAction(data, action === "release" ? (DONE.includes(data.fulfillment_status) ? "Final release confirmed. Order complete" : "Items released")
        : action === "reverse" ? "Pickup reversed" : "Order force-closed");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.needs_approval) {
        if (managerPin) throw new Error(d.message || "Approval required");
        setPin({ action, payload, title: "Manager Approval Required", reason: d.message });
        return;
      }
      toast.error(apiError(d));
      if (managerPin) throw new Error("Failed");
    } finally { setBusy(false); }
  };

  const release = () => {
    const lines = (detail.lines || []).map((l) => ({ index: l.index, quantity: parseFloat(rel[l.index] || 0) || 0 })).filter((l) => l.quantity > 0);
    if (!lines.length) { toast.error("Enter a quantity to release"); return; }
    run("release", { lines, collected_by: collectedBy, note });
  };

  const anyOver = (detail?.lines || []).some((l) => (parseFloat(rel[l.index] || 0) || 0) > l.remaining_qty + 1e-6);
  const isOpen = detail && !DONE.includes(detail.fulfillment_status);
  const yardPrint = (
    <span className="flex items-center gap-1 text-xs" data-testid="print-yard-ticket-block">
      <span className="text-muted-foreground flex items-center"><Printer className="w-4 h-4 mr-1" />Print Yard Ticket:</span>
      <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => printYardTicket(detail, business, "thermal80")} data-testid="print-yard-ticket-80">80mm</Button>
      <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => printYardTicket(detail, business, "thermal58")} data-testid="print-yard-ticket-58">58mm</Button>
    </span>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><PackageCheck className="w-7 h-7" /> Pickups &amp; Fulfillment</h1>
        <p className="text-muted-foreground">Paid goods waiting for pickup or delivery. Release items as they are collected or delivered. Take Now lines are already done.</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {TABS.map((t) => (
          <Button key={t.key} variant={tab === t.key && !q.trim() ? "default" : "outline"} size="sm"
            className={tab === t.key && !q.trim() ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
            onClick={() => { setTab(t.key); setQ(""); }} data-testid={`pickups-tab-${t.key}`}>
            <t.icon className="w-4 h-4 mr-1.5" />{t.label}
          </Button>
        ))}
        <div className="relative flex-1 min-w-[240px] max-w-md ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="pickups-search"
            placeholder="Invoice #, pickup ref, customer or phone…" />
        </div>
        {qrMode && (
          <Button onClick={() => setScanOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="scan-pickup-btn">
            <ScanLine className="w-4 h-4 mr-1.5" /> Scan Code
          </Button>
        )}
      </div>
      <PickupScanner open={scanOpen} onClose={() => setScanOpen(false)} onScan={onScan} />

      <Card className="p-0 overflow-hidden">
        <div className="divide-y divide-border">
          {rows.map((inv) => (
            <button key={inv.id} onClick={() => open(inv)} data-testid={`pickup-row-${inv.pickup_reference}`}
              className="w-full text-left px-4 py-3 hover:bg-secondary/50 flex flex-wrap items-center gap-3">
              <span className="mono font-bold w-28 text-amber-600 dark:text-amber-400">{inv.pickup_reference}</span>
              <span className="mono text-sm w-28">{inv.invoice_number}</span>
              <span className="text-sm text-muted-foreground w-24">{fmtDate(inv.sale_date)}</span>
              <span className="text-sm flex-1 min-w-[120px]">{inv.customer_name}</span>
              <span className="text-xs text-muted-foreground hidden md:block">{inv.remaining_total_qty > 0 ? `${inv.remaining_total_qty} unit(s) remaining` : "all collected"}</span>
              <Badge variant="outline" className="text-[10px]">{inv.payment_status}</Badge>
              <Badge className={`text-[10px] ${fulfillmentBadge(inv.fulfillment_status)}`}>{inv.fulfillment_label}</Badge>
              <span className="mono font-bold w-28 text-right">{money(inv.total)}</span>
            </button>
          ))}
          {rows.length === 0 && <p className="text-center text-muted-foreground py-10" data-testid="pickups-empty">No orders here.</p>}
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="pickup-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-head flex items-center justify-between gap-2">
                  <span className="mono">{detail.pickup_reference} <span className="text-muted-foreground text-sm font-normal">· {detail.invoice_number}</span></span>
                  <span className="flex gap-1.5">
                    <Badge variant="outline">{detail.payment_status}</Badge>
                    <Badge className={fulfillmentBadge(detail.fulfillment_status)} data-testid="pickup-status-badge">{detail.fulfillment_label}</Badge>
                  </span>
                </DialogTitle>
                <DialogDescription>{detail.customer_name} · {fmtDate(detail.sale_date)} · Sold by {detail.created_by}</DialogDescription>
              </DialogHeader>

              <div className="rounded-xl border border-border overflow-hidden">
                <div className="grid grid-cols-[1fr_70px_70px_70px_90px] gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary/40">
                  <span>Item</span><span className="text-right">Bought</span><span className="text-right">Done</span><span className="text-right">Left</span><span className="text-right">Release</span>
                </div>
                {detail.lines.map((l) => (
                  <div key={l.index} className={`grid grid-cols-[1fr_70px_70px_70px_90px] gap-2 items-center px-3 py-2 text-sm border-t border-border ${l.fulfillment === "take_now" ? "opacity-60" : ""}`} data-testid={`pickup-line-${l.index}`}>
                    <span className="font-medium truncate">{l.name}
                      <span className={`ml-1.5 text-[10px] font-semibold ${METHOD[l.fulfillment]?.cls || ""}`} data-testid={`pickup-line-method-${l.index}`}>{METHOD[l.fulfillment]?.label || l.method_label}</span>
                      {!l.reserved && l.fulfillment !== "take_now" && <span className="ml-1 text-[10px] text-muted-foreground">special order</span>}</span>
                    <span className="mono text-right">{l.purchased_qty}</span>
                    <span className="mono text-right">{l.picked_up_qty}</span>
                    <span className={`mono text-right font-bold ${l.remaining_qty > 0 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600"}`} data-testid={`pickup-remaining-${l.index}`}>{l.remaining_qty}</span>
                    {isOpen && l.fulfillment !== "take_now" ? (
                      <Input type="number" min="0" step="any" className="h-8 mono text-right" value={rel[l.index] ?? ""}
                        placeholder={String(l.remaining_qty)} onChange={(e) => setRel({ ...rel, [l.index]: e.target.value })}
                        data-testid={`release-qty-${l.index}`} />
                    ) : <span />}
                  </div>
                ))}
              </div>

              {isOpen && !closeMode && (
                <div className="space-y-2" data-testid="release-form">
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Collected by (optional)</Label><Input value={collectedBy} onChange={(e) => setCollectedBy(e.target.value)} placeholder="Name of person collecting" data-testid="collected-by" /></div>
                    <div><Label className="text-xs">Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Truck plate 1234AB" data-testid="release-note" /></div>
                  </div>
                  {anyOver && <p className="text-xs text-amber-600" data-testid="over-release-warn">Releasing more than remaining. Manager/Owner authority required.</p>}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { const all = {}; detail.lines.forEach((l) => { if (l.remaining_qty > 0) all[l.index] = String(l.remaining_qty); }); setRel(all); }} data-testid="release-all-btn">Fill all remaining</Button>
                    <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={release} data-testid="confirm-pickup-btn">
                      <PackageCheck className="w-4 h-4 mr-1.5" /> Confirm Release (Pickup / Delivery)
                    </Button>
                    <Button variant="ghost" size="sm" className="text-rose-500" onClick={() => setCloseMode(true)} data-testid="force-close-start"><Lock className="w-4 h-4 mr-1" />Force close</Button>
                  </div>
                </div>
              )}
              <div className="flex justify-end">{yardPrint}</div>
              {isOpen && closeMode && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 space-y-2" data-testid="force-close-form">
                  <p className="text-xs text-rose-500 font-semibold uppercase tracking-wider">Force-close with goods outstanding</p>
                  <p className="text-xs text-muted-foreground">Reservation is released and the order marked Closed. Requires Manager/Owner authority and is logged.</p>
                  <Input value={closeReason} onChange={(e) => setCloseReason(e.target.value)} placeholder="Reason (required)" data-testid="force-close-reason" />
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" onClick={() => setCloseMode(false)}>Cancel</Button>
                    <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={!closeReason.trim() || busy} onClick={() => run("force-close", { reason: closeReason })} data-testid="force-close-confirm">Force Close</Button>
                  </div>
                </div>
              )}
              {detail.force_closed && <p className="text-xs text-rose-500">Force-closed: {detail.force_close_reason}</p>}

              <div className="space-y-1" data-testid="pickup-history">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fulfillment History</p>
                {detail.pickup_history.length === 0 && <p className="text-xs text-muted-foreground">No releases recorded yet.</p>}
                {detail.pickup_history.map((ev) => (
                  <div key={ev.id} className={`rounded-lg border border-border px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 ${ev.reversed ? "opacity-50 line-through" : ""}`} data-testid={`pickup-event-${ev.id}`}>
                    <Badge variant="outline" className="text-[10px] capitalize">{ev.type.replace("_", " ")}</Badge>
                    <span className="text-muted-foreground">{new Date(ev.created_at).toLocaleString()}</span>
                    <span className="flex-1">{ev.type === "force_close" ? ev.note : ev.lines.map((l) => `${l.name} ×${l.quantity}`).join(", ")}</span>
                    <span>by {ev.staff_name}{ev.approved_by && ev.approved_by !== ev.staff_name ? ` (appr. ${ev.approved_by})` : ""}</span>
                    {ev.collected_by && <span>· collected by {ev.collected_by}</span>}
                    {ev.note && ev.type !== "force_close" && <span className="text-muted-foreground">· {ev.note}</span>}
                    {ev.type === "release" && !ev.reversed && detail.fulfillment_status !== "closed" && (
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-rose-500" disabled={busy}
                        onClick={() => run("reverse", { event_id: ev.id, reason: isMgr ? "Reversed by manager" : "Reversed with approval" })}
                        data-testid={`reverse-event-${ev.id}`}><RotateCcw className="w-3 h-3 mr-1" />Reverse</Button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ManagerPinModal open={!!pin} onClose={() => setPin(null)} title={pin?.title} reason={pin?.reason}
        onSubmit={(p) => run(pin.action, pin.payload, p)} />
    </div>
  );
}
